/**
 * 資料請求: 入力 → 確認 → 完了（静的ビルド用 sessionStorage 連携）
 */
import { validateDocumentRequestForm, initDocumentRequestFormValidation } from './document-request-form-validation.js';

const STORAGE_KEY = 'takamatsu_document_request_v1';
const CONFIRM_PATH = '/document_request_confirm.html';
const COMPLETE_PATH = '/document_request_complete.html';

/** デザイン確認用。本番では false にし、データ無し時は empty を表示 */
const DOC_REQ_CONFIRM_PREVIEW_ALWAYS_VISIBLE = true;

const INQUIRY_LABELS = {
	detail: '物件の詳細が知りたい',
	visit: '物件を見学したい',
	match: '希望条件に合う物件を紹介して欲しい',
	loan: '\u30ed\u30fc\u30f3\u3001\u3054\u8cfc\u5165\u306b\u95a2\u3057\u3066\u76f8\u8ac7\u3057\u305f\u3044',
};

function getFormRoot() {
	return document.querySelector('form[data-document-request-flow]#document-request-form');
}

function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	const checks = form.querySelectorAll('input[name="docreq_inquiry[]"]:checked');
	const inquiry = [...checks].map((cb) => cb.value);
	return {
		docreq_inquiry: inquiry,
		docreq_message: pick('docreq_message'),
		docreq_name_family: pick('docreq_name_family').trim(),
		docreq_name_given: pick('docreq_name_given').trim(),
		docreq_tel: pick('docreq_tel').trim(),
		docreq_email: pick('docreq_email').trim(),
		docreq_zip: pick('docreq_zip').trim(),
		docreq_pref: pick('docreq_pref').trim(),
		docreq_city: pick('docreq_city').trim(),
		docreq_street: pick('docreq_street').trim(),
		docreq_building: pick('docreq_building').trim(),
		docreq_privacy: fd.get('docreq_privacy') === '1',
	};
}

function collectInquiryDisplay(form) {
	const checks = form.querySelectorAll('input[name="docreq_inquiry[]"]:checked');
	const labels = [...checks]
		.map((cb) => INQUIRY_LABELS[cb.value] || '')
		.filter(Boolean);
	return labels.length ? labels.join('\u3001') : '\u2014';
}

function collectFormPayload(form) {
	const fd = new FormData(form);
	const prefEl = form.querySelector('#docreq-pref');
	const prefLabel =
		prefEl && prefEl.selectedIndex >= 0 ? prefEl.options[prefEl.selectedIndex].text.trim() : '';
	const family = (fd.get('docreq_name_family') || '').toString().trim();
	const given = (fd.get('docreq_name_given') || '').toString().trim();
	const nameFull = [family, given].filter(Boolean).join(' ') || '\u2014';

	const zipRaw = (fd.get('docreq_zip') || '').toString().trim();
	const postalMark = '\u3012';
	const zipDisplay = zipRaw
		? `${postalMark} ${zipRaw.replace(new RegExp(`^${postalMark}\\s*`), '')}`
		: '';

	const pref = (fd.get('docreq_pref') || '').toString().trim();
	const city = (fd.get('docreq_city') || '').toString().trim();
	const street = (fd.get('docreq_street') || '').toString().trim();
	const building = (fd.get('docreq_building') || '').toString().trim();
	const prefDisplay = prefLabel && prefLabel !== '\u90fd\u9053\u5e9c\u770c' ? prefLabel : pref || '';

	const line2 = [prefDisplay, city, street].filter(Boolean).join(' ');
	const lines = [zipDisplay, line2, building].filter((line) => line && line.length > 0);
	const addressBlock = lines.length ? lines.join('\n') : '\u2014';

	return {
		docreq_inquiry_display: collectInquiryDisplay(form),
		docreq_message: (fd.get('docreq_message') || '').toString().trim() || '\u2014',
		docreq_name_full: nameFull,
		docreq_tel: (fd.get('docreq_tel') || '').toString().trim() || '\u2014',
		docreq_email: (fd.get('docreq_email') || '').toString().trim() || '\u2014',
		docreq_address_block: addressBlock,
		docreq_privacy_agreed: fd.get('docreq_privacy') === '1',
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearDraft() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	if (Array.isArray(raw.docreq_inquiry)) {
		form.querySelectorAll('input[name="docreq_inquiry[]"]').forEach((cb) => {
			cb.checked = raw.docreq_inquiry.includes(cb.value);
		});
	}

	if (Object.prototype.hasOwnProperty.call(raw, 'docreq_privacy')) {
		const cb = form.querySelector('input[name="docreq_privacy"]');
		if (cb) cb.checked = Boolean(raw.docreq_privacy);
	}

	const skip = new Set(['docreq_inquiry', 'docreq_privacy']);
	for (const key of Object.keys(raw)) {
		if (skip.has(key)) continue;
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;
		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function initFormPage() {
	const form = getFormRoot();
	if (!form) return;

	initDocumentRequestFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		applyRestoreToForm(form, draft._restore);
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validateDocumentRequestForm(form)) {
			return;
		}
		const confirmPayload = collectFormPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({ ...confirmPayload, _restore: raw });
		window.location.href = CONFIRM_PATH;
	});
}

function setPreFormattedDesc(el, text) {
	if (!el) return;
	el.replaceChildren();
	const lines = String(text).split('\n');
	lines.forEach((line) => {
		const span = document.createElement('span');
		span.className = 'visit__confirmPreLine';
		span.textContent = line;
		el.appendChild(span);
	});
}

function setTextContent(el, text) {
	if (!el) return;
	el.textContent = text;
}

function initConfirmPage() {
	const page = document.querySelector('[data-document-request-page="confirm"]');
	if (!page) return;

	const block = document.getElementById('document-request-confirm-block');
	const empty = document.getElementById('document-request-confirm-empty');
	const shell = document.querySelector('.documentRequest__confirmShell');
	const draft = loadDraft();

	if (!block || !empty) return;

	const sendBtn = document.getElementById('document-request-confirm-send');
	if (sendBtn) {
		sendBtn.addEventListener('click', () => {
			clearDraft();
			window.location.assign(COMPLETE_PATH);
		});
	}

	if (!draft) {
		if (DOC_REQ_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			if (shell) shell.hidden = false;
			block.hidden = false;
			empty.hidden = true;
		} else {
			if (shell) shell.hidden = true;
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	const dash = '\u2014';
	const hasRequired =
		draft.docreq_name_full !== dash &&
		draft.docreq_email !== dash &&
		draft.docreq_address_block !== dash;

	if (!hasRequired) {
		if (DOC_REQ_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			if (shell) shell.hidden = false;
			block.hidden = false;
			empty.hidden = true;
		} else {
			if (shell) shell.hidden = true;
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	if (shell) shell.hidden = false;
	block.hidden = false;
	empty.hidden = true;

	page.querySelectorAll('[data-docreq-field]').forEach((el) => {
		const key = el.getAttribute('data-docreq-field');
		if (!key || !(key in draft)) return;
		const val = draft[key];
		const out = typeof val === 'boolean' ? (val ? '\u540c\u610f\u6e08\u307f' : dash) : String(val);
		if (el.classList.contains('visit__confirmDesc--pre')) {
			setPreFormattedDesc(el, out);
		} else {
			setTextContent(el, out);
		}
	});
}

export function initDocumentRequestFlow() {
	const pageType = document
		.querySelector('[data-document-request-page]')
		?.getAttribute('data-document-request-page');
	if (!pageType) return;
	if (pageType === 'form') initFormPage();
	if (pageType === 'confirm') initConfirmPage();
}
